import { Card, FormattedMessageDiv, Button } from '@odmonorepo/core-components';
import { Stack, Collapse } from 'react-bootstrap';
import { useNavigate } from '@tanstack/react-router';
import { loanStep } from '../helpers';
import { steps } from '../LoanAppForm';
import { getLDR, getRenewal, getSalesAgentInfo, useLoan, useParties } from 'merchant-api/spiceroad';
import { getCurrentAppOpts, useApplication } from 'merchant-api/unified';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { trackCheckoutBlockingError } from 'merchant-components';
import { useAnalytics } from 'use-analytics';
import { AppError, queryClient, unifiedApiClient } from 'merchant-api';
import { Application } from 'merchant-api/types';


export const RenewalNotification = () => {
  const { step } = loanStep();
  const { data: applicationData, status } = useQuery({...getCurrentAppOpts(),
    staleTime: 0,
    gcTime: 0
  });
  const { loan } = useLoan();
  const { renewal } = getRenewal();
  const { isLoadingParties, isMultiOwner } = useParties();
  const { loanDocRequest, ldrLoading } = getLDR();

  if (ldrLoading || isLoadingParties) return null;
  if (loanDocRequest?.some((ldr) => ldr.classification === 'CLOSING' && (ldr.status === 'REQUESTED' || ldr.status === 'MERCHANT_RESPONSE_SUBMITTED' || ldr.status === 'REJECTED'))) return null;

  const showRenewal = applicationData?.status !== 'APPROVED' && loan?.percentagePaidDown >= 25 && renewal?.termLoanEligible;
  if (status === "success" && step === "DECLINED" && applicationData?.metadata?.referralSource !== 'Pre-approved Renewal') return null;

  return (
    <Collapse in={showRenewal}>
      <Card bg="primary" text="light" className="text-center" dismissible dismissItem="renewalNotificationDismissed">
        <Stack gap={3}>
          {isMultiOwner ? <MultiOwnerRenewalNotification/> : <FundingStatus status={step} showRenewal={showRenewal} /> }
        </Stack>
      </Card>
    </Collapse>
  );
};

const Submitted = (props: {showRenewal?: boolean}) => {
  const {showRenewal} = props;
  const salesAgentInfoProps = getSalesAgentInfo();
  const useApplicationProps = useApplication();
  const analytics = useAnalytics();

  useEffect(() => {
    if (!showRenewal) return;
    trackCheckoutBlockingError(analytics, AppError.ApplicationPendingApproval, salesAgentInfoProps, useApplicationProps);
  }, [salesAgentInfoProps.salesAgentInfo, useApplicationProps.opportunityId, showRenewal]);

  return <>
    <FormattedMessageDiv className="h3" id="notification.title.submitted" />
    <FormattedMessageDiv id="notification.message.submitted" />
  </>;
};

const Started = () => {
  const navigate = useNavigate();
  const { data: applicationData, isLoading } = useQuery({...getCurrentAppOpts()});
  return (
    <>
      <FormattedMessageDiv className="h3" id="notification.title.started" />
      <FormattedMessageDiv id="notification.message.started" />
      <div>
        <Button variant="outline-light" disabled={isLoading} onClick={() => navigate({to: '/application/$id', params: { id: applicationData!.id }})}>
          <FormattedMessageDiv id="notification.started" />
        </Button>
      </div>
    </>
  );
};

const Renewal = () => {
  const navigate = useNavigate();
  const { partyId } = useParties();
  const { data: applicationData, status } = useQuery({...getCurrentAppOpts(),
    staleTime: 0,
    gcTime: 0
  });
  const { mutate: createApp } = useMutation({
    mutationFn: async (partyId?: string) => {
      const { data } = await unifiedApiClient.POST('/client/application/v1/create', { body: { partyId } as never });
      // Butterfly init can answer 201 without echoing the app, so refetch instead of seeding the cache.
      queryClient.invalidateQueries({ queryKey: ['app'] });
      return data as unknown as Application;
    },
    onSuccess: (data) => navigate({ to: '/application/$id', params: { id: data.id } })
   });

  const handleClick = async () => {
    if(status === "success" && (!applicationData || applicationData?.status === 'CLOSED_LOST')) createApp(partyId)
  }

  return (
    <>
      <FormattedMessageDiv className="h3" id="notification.title.renewal" />
      <FormattedMessageDiv id="notification.message.renewal" />
      <div>
        <Button variant="outline-light" onClick={() => handleClick()}>
          <FormattedMessageDiv id="notification.renewal" />
        </Button>
      </div>
    </>
  );
};

interface FundingStatusProps {
  status?: keyof typeof steps;
  showRenewal?: boolean;
}

const FundingStatus = ({status, showRenewal}: FundingStatusProps) => {
  switch (status) {
    case "RECEIVED": return <Submitted showRenewal={showRenewal}/>;
    case "BANK_INFO":
    case "BANK_CONFIRMATION":
    case "CONSENT":
    case "BUSINESS_CONTACT_STEP":
    case "FINANCE_STEP":
    case "BUSINESS_STEP":
    case "OWNER_STEP":
      return <Started />;
    default: return <Renewal />;
  }
};

const MultiOwnerRenewalNotification = () => {
  return <>
    <FormattedMessageDiv className="h3" id="notification.title.renewal" />
    <FormattedMessageDiv id="notification.message.multiOwnerRenewal" />
  </>
}

export default RenewalNotification;
